import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Shield } from 'lucide-react'
import HomePage from './pages/HomePage'
import TrustCenterEnhanced from './pages/TrustCenterEnhanced'
import EvidenceLibrary from './pages/EvidenceLibrary'
import SubprocessorsPage from './pages/SubprocessorsPage'
import StatusPage from './pages/StatusPage'
import AdminDashboard from './pages/AdminDashboard'
import AdminDebug from './pages/AdminDebug'
import AdminSimple from './pages/AdminSimple'
import AdminFullFunctional from './pages/AdminFullFunctional'
import PlatformAdmin from './pages/PlatformAdmin'
import TrustCentersDirectory from './pages/TrustCentersDirectory'
import PricingPage from './pages/PricingPage'
import BlogPage from './pages/BlogPage'
import BlogPostPage from './pages/BlogPostPage'
import LoginPage from './pages/LoginPage'
import MagicLinkPage from './pages/MagicLinkPage'
import UserProfilePage from './pages/UserProfilePage'
import TermsOfUsePage from './pages/TermsOfUsePage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import DmcaSubmitPage from './pages/DmcaSubmitPage'
import DmcaCounterPage from './pages/DmcaCounterPage'
import DocumentViewPage from './pages/DocumentViewPage'
import ChangeLogPage from './pages/ChangeLogPage'
import ChangelogDetailPage from './pages/ChangelogDetailPage'
import AutomationRulesPage from './pages/AutomationRulesPage'
import QuestionnaireLibrary from './pages/QuestionnaireLibrary'
import KnowledgeBasePage from './pages/KnowledgeBasePage'
import PrivacyPortalPage from './pages/PrivacyPortalPage'
import SubmitAssessmentPage from './pages/SubmitAssessmentPage'
import PolicyGeneratorWizard from './pages/PolicyGeneratorWizard'
import AgentRegistryPage from './pages/AgentRegistryPage'
import AgentProfilePage from './pages/AgentProfilePage'
import { TenantProvider } from './contexts/TenantContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { ContentRefreshProvider } from './contexts/ContentRefreshContext'
import ProtectedTenantRoute from './components/ProtectedTenantRoute'
import VersionBanner from './components/VersionBanner'
import SessionManager from './components/SessionManager'
import { installFetchInterceptor } from './lib/api'

// Check if this is a tenant site (branch deploy) or platform site (main)
const isTenantSite = () => {
  // @ts-ignore - __TENANT_CONFIG__ is injected at build time for branch deploys
  if (typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__?.tenantId) {
    return true
  }
  // Fallback: URL-pattern detection for when tenant-config.json was null at build time
  // (e.g., config write failed or merge conflict handling went wrong)
  const hostname = window.location.hostname
  // Tenant branch deploy URLs: {name}-(live|preview|last)-{digits}.trustcenterpage.com
  if (/-(live|preview|last)-\d+\.trustcenterpage\.com$/.test(hostname)) {
    return true
  }
  // Custom domains (not the platform domain, not localhost)
  if (hostname !== 'trustcenterpage.com' &&
      !hostname.endsWith('.trustcenterpage.com') &&
      hostname !== 'localhost' &&
      !hostname.startsWith('127.') &&
      !hostname.startsWith('192.168.')) {
    return true
  }
  return false
}

// Component to manage robots meta tag based on version
const RobotsMetaTag = () => {
  useEffect(() => {
    // @ts-ignore - __TENANT_CONFIG__ is injected at build time for branch deploys
    // Safety improvement: Use optional chaining to prevent TypeError if __TENANT_CONFIG__ is null
    if (typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__?.version) {
      const version = __TENANT_CONFIG__.version

      // Only allow indexing on live (production) sites
      // Block preview and last (rollback) versions from search engines
      if (version === 'preview' || version === 'last') {
        // Remove existing robots meta tag if present
        const existingTag = document.querySelector('meta[name="robots"]')
        if (existingTag) {
          existingTag.remove()
        }

        // Add noindex, nofollow for non-production versions
        const meta = document.createElement('meta')
        meta.name = 'robots'
        meta.content = 'noindex, nofollow'
        document.head.appendChild(meta)

        console.log(`🤖 Robots: noindex applied for ${version} version`)
      } else if (version === 'live') {
        // Ensure no noindex tag exists for live version
        const existingTag = document.querySelector('meta[name="robots"]')
        if (existingTag) {
          existingTag.remove()
        }
        console.log('🤖 Robots: indexable (live version)')
      }
    }
  }, [])

  return null
}

// Platform marketing landing page component
const PlatformLanding = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Header */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-blue-400" />
              <a href="/" className="ml-3 text-xl sm:text-2xl font-bold text-white">TrustCenterPage.com</a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a href="/directory" className="text-gray-300 hover:text-white">Directory</a>
              <a href="/registry" className="text-gray-300 hover:text-white">Agent Registry</a>
              <a href="/pricing" className="text-gray-300 hover:text-white">Pricing</a>
              <a href="/admin" className="text-gray-300 hover:text-white">Company Login</a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800"
            >
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
              <div className="flex flex-col gap-3">
                <a href="/directory" className="text-gray-300 hover:text-white px-4 py-2 rounded-md hover:bg-gray-800">Directory</a>
                <a href="/registry" className="text-gray-300 hover:text-white px-4 py-2 rounded-md hover:bg-gray-800">Agent Registry</a>
                <a href="/pricing" className="text-gray-300 hover:text-white px-4 py-2 rounded-md hover:bg-gray-800">Pricing</a>
                <a href="/admin" className="text-gray-300 hover:text-white px-4 py-2 rounded-md hover:bg-gray-800">Company Login</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Trust for Humans<br />
            <span className="text-blue-400">and Machines.</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            The trust center platform built for the AI era. Human reviewers get compliance docs, NDA workflows,
            and audit reports. AI agents get machine-readable APIs, cryptographic proofs, and security scanning —
            all from one place.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a
              href="/registry"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition"
            >
              Browse Agent Registry
            </a>
            <a
              href="/admin"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition"
            >
              Register Your Agent
            </a>
            <a
              href="/pricing"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition"
            >
              View Pricing
            </a>
            <a
              href="https://calendar.app.google/mtvgJVaXPtfD95dcA"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition"
            >
              Book a Demo
            </a>
          </div>
        </div>

        {/* Who We Serve */}
        <div className="mt-24 grid md:grid-cols-2 gap-8">
          {/* Human Teams */}
          <div className="bg-blue-900/30 border border-blue-500/40 rounded-xl p-8">
            <div className="flex items-center mb-6">
              <span className="text-3xl mr-3">👥</span>
              <h3 className="text-xl font-bold text-white">For Human Teams</h3>
            </div>
            <ul className="space-y-3">
              {[
                ['📋', 'Compliance docs, audit reports & certificates'],
                ['📝', 'NDA workflows with 5 professional templates'],
                ['🤖', 'AI-powered CAIQ v4.0.3 knowledge base (261 questions)'],
                ['🔗', 'CRM-connected deal acceleration (Salesforce, HubSpot)'],
                ['🏅', 'SOC 2, ISO 27001, GDPR, HIPAA attestations'],
                ['🔄', 'Version control with preview / live / rollback'],
              ].map(([icon, text], i) => (
                <li key={i} className="flex items-start text-sm text-gray-300">
                  <span className="mr-2 mt-0.5 flex-shrink-0">{icon}</span>
                  {text}
                </li>
              ))}
            </ul>
          </div>
          {/* AI Systems */}
          <div className="bg-gray-800/60 border border-gray-600 rounded-xl p-8">
            <div className="flex items-center mb-6">
              <span className="text-3xl mr-3">🤖</span>
              <h3 className="text-xl font-bold text-white">For AI Systems</h3>
            </div>
            <ul className="space-y-3">
              {[
                ['🔌', 'Machine-readable REST APIs & native MCP JSON-RPC'],
                ['🔐', 'Zero-knowledge proofs of skill integrity (PLONK)'],
                ['🔍', 'AI agent security scanning — OWASP LLM Top 10'],
                ['⚠️', 'Lethal trifecta detection (private data + untrusted input + exfiltration)'],
                ['📡', 'OpenTelemetry-compatible security telemetry export'],
                ['🏅', 'Cryptographic rank & skill attestation badges'],
              ].map(([icon, text], i) => (
                <li key={i} className="flex items-start text-sm text-gray-300">
                  <span className="mr-2 mt-0.5 flex-shrink-0">{icon}</span>
                  {text}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Features Section */}
        <div id="features" className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
            <div className="text-blue-400 text-3xl mb-4">🤖</div>
            <h3 className="text-xl font-bold text-white mb-3">Agent Identity</h3>
            <p className="text-gray-400">
              Register AI agents, MCP servers, and service accounts with their declared capabilities, data access scope, and trust scores.
            </p>
          </div>
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
            <div className="text-blue-400 text-3xl mb-4">🔌</div>
            <h3 className="text-xl font-bold text-white mb-3">MCP Native</h3>
            <p className="text-gray-400">
              Query the registry via REST API or native MCP JSON-RPC. AI agents can verify each other's trust profiles before connecting.
            </p>
          </div>
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
            <div className="text-blue-400 text-3xl mb-4">🏅</div>
            <h3 className="text-xl font-bold text-white mb-3">Compliance Badges</h3>
            <p className="text-gray-400">
              Expose SOC 2, ISO 27001, GDPR, and custom compliance certifications. Metered tiers unlock deep audit evidence.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to build trust?</h2>
          <p className="text-gray-300 mb-8">One platform. Every principal. Start free.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a
              href="/admin"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition inline-block"
            >
              Get Started Free
            </a>
            <a
              href="/pricing"
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition inline-block"
            >
              View Pricing
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900/50 backdrop-blur-sm border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} Trust Center Platform. Built with Netlify.</p>
        </div>
      </footer>
    </div>
  )
}

function App() {
  const tenantMode = isTenantSite()
  // Check if this is the platform admin build (via environment variable)
  const isPlatformAdminBuild = import.meta.env.VITE_IS_PLATFORM_ADMIN === 'true'

  // Install global fetch interceptor for session timeout handling
  useEffect(() => {
    installFetchInterceptor()
  }, [])

  return (
    <LanguageProvider>
      <RobotsMetaTag />
      <VersionBanner />
      <Router>
        <div className="min-h-screen bg-gray-50">
          <TenantProvider>
            {/* Session Management - Auto-refresh tokens and handle timeouts */}
            <SessionManager enabled={!tenantMode} />
            <Routes>
              {tenantMode ? (
                // TENANT MODE: Show public routes for tenant content
                <>
                  {/* Login route is always accessible (no protection) */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/magic/:code" element={<MagicLinkPage />} />
                  <Route path="/profile" element={<ProtectedTenantRoute><UserProfilePage /></ProtectedTenantRoute>} />

                  {/* Protected routes - require auth for preview/last sites */}
                  <Route path="/" element={<ProtectedTenantRoute><TrustCenterEnhanced /></ProtectedTenantRoute>} />
                  <Route path="/basic" element={<ProtectedTenantRoute><HomePage /></ProtectedTenantRoute>} />
                  <Route path="/evidence" element={<ProtectedTenantRoute><EvidenceLibrary /></ProtectedTenantRoute>} />
                  <Route path="/documents/:documentId" element={<ProtectedTenantRoute><DocumentViewPage /></ProtectedTenantRoute>} />
                  <Route path="/subprocessors" element={<ProtectedTenantRoute><SubprocessorsPage /></ProtectedTenantRoute>} />
                  <Route path="/status" element={<ProtectedTenantRoute><StatusPage /></ProtectedTenantRoute>} />
                  <Route path="/changelog" element={<ProtectedTenantRoute><ChangeLogPage /></ProtectedTenantRoute>} />
                  <Route path="/changelog/:slug" element={<ProtectedTenantRoute><ChangelogDetailPage /></ProtectedTenantRoute>} />
                  <Route path="/terms" element={<ProtectedTenantRoute><TermsOfUsePage /></ProtectedTenantRoute>} />
                  <Route path="/privacy" element={<ProtectedTenantRoute><PrivacyPolicyPage /></ProtectedTenantRoute>} />
                  <Route path="/privacy-portal" element={<PrivacyPortalPage />} />
                  <Route path="/dmca-submit" element={<ProtectedTenantRoute><DmcaSubmitPage /></ProtectedTenantRoute>} />
                  <Route path="/dmca-counter" element={<ProtectedTenantRoute><DmcaCounterPage /></ProtectedTenantRoute>} />

                  {/* Redirect admin routes to prevent access on tenant sites */}
                  <Route path="/admin/*" element={<Navigate to="/" replace />} />
                </>
              ) : (
              // PLATFORM MODE: Show marketing, platform admin, and tenant admin routes
              <>
                {/* Platform Admin Build: Show PlatformAdmin at root */}
                {isPlatformAdminBuild ? (
                  <>
                    <Route path="/" element={<PlatformAdmin />} />
                    <Route path="/platformadmin" element={<PlatformAdmin />} />
                  </>
                ) : (
                  <>
                    {/* Marketing Site: Show landing page and directory */}
                    <Route path="/" element={<PlatformLanding />} />
                    <Route path="/directory" element={<TrustCentersDirectory />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/privacy-portal" element={<PrivacyPortalPage />} />
                    <Route path="/registry" element={<AgentRegistryPage />} />
                    <Route path="/registry/:agentId" element={<AgentProfilePage />} />
                    <Route path="/platformadmin" element={<PlatformAdmin />} />
                  </>
                )}

                {/* Tenant admin routes (TenantProvider wraps all routes) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/platformadmin/tenant/:tenantId" element={<ContentRefreshProvider><AdminFullFunctional /></ContentRefreshProvider>} />
                <Route path="/admin" element={<ContentRefreshProvider><AdminFullFunctional /></ContentRefreshProvider>} />
                <Route path="/admin/policy-generator" element={<PolicyGeneratorWizard />} />
                <Route path="/admin/contracts" element={<Navigate to="/admin#tprm" replace />} />
                <Route path="/tprm" element={<Navigate to="/admin#tprm" replace />} />
                <Route path="/submit-assessment" element={<SubmitAssessmentPage />} />
                <Route path="/admin/rules" element={<AutomationRulesPage />} />
                <Route path="/admin/questionnaires" element={<QuestionnaireLibrary />} />
                <Route path="/admin/knowledge-base" element={<KnowledgeBasePage />} />
                <Route path="/admin/simple" element={<AdminSimple />} />
                <Route path="/admin/legacy" element={<AdminDashboard />} />
                <Route path="/admin/debug" element={<AdminDebug />} />
                {/* Redirect unknown routes to home on platform site */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
            </Routes>
          </TenantProvider>
        </div>
      </Router>
    </LanguageProvider>
  )
}

export default App